import { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { Portal } from '@mui/material';
import CalendarIcon from 'assets/LeadInfo/calendar.svg';
import assignLead from 'assets/myLeads/assignLead.svg';
import reassignLead from 'assets/myLeads/reassignLead.svg';
import takeLead from 'assets/myLeads/takeLead.svg';
import { selectUserInfo, selectUserType } from 'features/auth/authSlice';
import more from 'pages/finance/financeRequest/components/actions/assets/more.svg';

import { Container, Dropdown, Icon, MenuIcon, Option } from './Actions.styles';

const ASSIGN_ROLES = [
  'Super Admin',
  'General Manager',
  'Department Head',
  'Branch Head',
  'Team Lead',
];

const Actions = ({
  setMode,
  onView,
  onTake,
  onAppointment,
  dropdownPosition = 'down',
  lead,
}) => {
  const userType = useSelector(selectUserType);
  const userInfo = useSelector(selectUserInfo);
  const canAssign = ASSIGN_ROLES.includes(userType);
  const isOwnLead = lead?.assigneeUuid && lead.assigneeUuid === userInfo?.uuid;
  const canReassign = canAssign || isOwnLead;
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const calculateDropdownPosition = () => {
    if (!containerRef.current) return {};

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 160;
    const optionHeight = 36;
    const padding = 26;
    const actualDropdownHeight = optionHeight + padding;

    let top =
      dropdownPosition === 'up' ? rect.top - actualDropdownHeight + 15 : rect.bottom + 5;
    let left = rect.right - dropdownWidth;

    if (left < 0) left = rect.left;
    if (left + dropdownWidth > viewportWidth) left = viewportWidth - dropdownWidth - 10;

    return { top: top - 10, left: left + 10 };
  };

  const toggleDropdown = (event) => {
    event.stopPropagation();
    setIsOpen((prev) => {
      if (!prev) setDropdownStyle(calculateDropdownPosition());
      return !prev;
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setDropdownStyle(calculateDropdownPosition());
    };
    const handleResize = () => {
      if (isOpen) setDropdownStyle(calculateDropdownPosition());
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const handleView = (e, mode) => {
    setMode(mode);
    e?.preventDefault();
    e?.stopPropagation();
    onView?.(lead);
    setIsOpen(false);
  };

  const handleTakeLead = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsOpen(false);
    onTake?.(lead);
  };

  const handleAppointmentClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsOpen(false);
    onAppointment?.(lead);
  };

  return (
    <Container
      ref={containerRef}
      $isOpen={isOpen}
      $relative={isOpen}
      onMouseEnter={toggleDropdown}
      onMouseLeave={toggleDropdown}
    >
      <Icon src={more} alt="more options" />
      {isOpen && (
        <Portal>
          <Dropdown style={dropdownStyle}>
            {!lead?.assigneeUuid
              ? canAssign && (
                  <Option onClick={(e) => handleView(e, 'assign')}>
                    <MenuIcon src={assignLead} alt="Assign" />
                    <p>Assign</p>
                  </Option>
                )
              : canReassign && (
                  <Option onClick={(e) => handleView(e, 'reassign')}>
                    <MenuIcon src={reassignLead} alt="Reassign" />
                    <p>Reassign</p>
                  </Option>
                )}
            {!lead?.assigneeUuid && (
              <Option className="take-lead" onClick={handleTakeLead}>
                <MenuIcon src={takeLead} alt="Take" />
                <p>Take Lead</p>
              </Option>
            )}
            {lead.allowAppointment && (
              <Option onClick={handleAppointmentClick}>
                <MenuIcon src={CalendarIcon} alt="Appointment" />
                <p>Appointment</p>
              </Option>
            )}
          </Dropdown>
        </Portal>
      )}
    </Container>
  );
};

export default Actions;
